import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useRef, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  CartesianGrid
} from 'recharts';

interface ShareChartsProps {
  chartByDay: { date: string; copies: number }[];
  topStates: { state: string; copies: number }[];
}

function ChartContainer({ children, height = 256 }: { children: (width: number) => React.ReactNode; height?: number }) {
  useLanguage();
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (ref.current) setWidth(ref.current.offsetWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: '100%', height }}>
      {width > 0 && children(width)}
    </div>
  );
}

export function ShareCharts({ chartByDay, topStates }: ShareChartsProps) {
  useLanguage();
  return (
    <div className="grid gap-6 md:grid-cols-2 mb-8">
      {/* Line Chart - Copies per Day */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{translateInterfaceText("Cópias por Dia")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer height={256}>
            {(width) => chartByDay.every(item => item.copies === 0) ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    {translateInterfaceText("Nenhuma cópia registrada ainda")}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {translateInterfaceText("O gráfico será atualizado quando houver atividade")}</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width={width} height={256}>
                <LineChart data={chartByDay} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value: number) => [translateInterfaceText("{count} cópias", { count: value }), translateInterfaceText("Cópias")]}
                  />
                  <Line
                    type="monotone"
                    dataKey="copies"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Bar Chart - Top States */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{translateInterfaceText("Top Estados")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer height={256}>
            {(width) => topStates.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    {translateInterfaceText("Nenhum dado de localização disponível")}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {translateInterfaceText("As informações aparecerão quando houver cópias do vídeo")}</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width={width} height={256}>
                <BarChart
                  data={topStates}
                  layout="vertical"
                  margin={{ top: 0, right: 20, bottom: 0, left: 0 }}
                  barGap={8}
                >
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    hide
                  />
                  <YAxis
                    type="category"
                    dataKey="state"
                    tick={{ fontSize: 13, fill: 'hsl(var(--foreground))', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                    width={90}
                  />
                  <RechartsTooltip
                    cursor={{ fill: 'transparent' }}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      padding: '8px 12px'
                    }}
                    labelStyle={{ display: 'none' }}
                    formatter={(value: number) => [translateInterfaceText("{count} cópias", { count: value }), '']}
                  />
                  <Bar
                    dataKey="copies"
                    fill="url(#barGradient)"
                    radius={[4, 8, 8, 4]}
                    barSize={28}
                    animationDuration={800}
                    animationEasing="ease-out"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}
